import { useEffect, useState } from "react"
import { getActiveSyncEngine } from "./reset"

export interface UseEmailBodyOnDemandResult {
  /** True while the body is being fetched. */
  loading: boolean
  /** True when the body couldn't be fetched (offline / network error). */
  offlineMiss: boolean
}

/**
 * Open-before-backfill email BODY fetch (STK-1325).
 *
 * When `enabled` (the opened email's body isn't in Dexie yet — it hasn't been
 * background-backfilled), fires a one-shot `engine.fetchEmailBody(id)` which
 * merges the body into the email row. The BODY itself is read by the caller's
 * own `useEmail(id)` reactive read — it re-fires when the merge lands — so this
 * hook only owns the `loading` / `offlineMiss` status. Disabled → idle (an
 * already-backfilled email never hits the network).
 */
export function useEmailBodyOnDemand(
  emailId: string | null,
  enabled: boolean
): UseEmailBodyOnDemandResult {
  const [state, setState] = useState<UseEmailBodyOnDemandResult>({
    loading: false,
    offlineMiss: false,
  })

  useEffect(() => {
    if (!enabled || !emailId) {
      setState({ loading: false, offlineMiss: false })
      return
    }
    const engine = getActiveSyncEngine()
    if (!engine) {
      setState({ loading: false, offlineMiss: true })
      return
    }

    let cancelled = false
    setState({ loading: true, offlineMiss: false })
    engine
      .fetchEmailBody(emailId)
      .then((res) => {
        if (!cancelled)
          setState({ loading: false, offlineMiss: res.offlineMiss })
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, offlineMiss: true })
      })
    return () => {
      cancelled = true
    }
  }, [emailId, enabled])

  return state
}
